/**
 * Product→impact causal "pathway" diagram: a tiered SVG of nodes (product at
 * the top, the impact at the bottom, mechanism steps between) connected by
 * causal edges. Renders the `pathwayData` returned by the product-impact
 * reasoning service.
 *
 * Ported from the chat MCP iframe renderer
 * (`src/ui/mcp-apps/product-impact-reasoning/app.tsx`) so the platform can show
 * the same diagram outside chat. The MCP app keeps its own copy on purpose —
 * that iframe is a separate bundle that deliberately avoids importing platform
 * UI. Pure React + inline SVG, no external deps beyond the shared pathway type.
 */
import { ProductImpactPathwayData } from '@upright/common/model/output/scores/drilldowns/leena2-impact-reasoning-types';
import { useMemo } from 'react';
import styled from 'styled-components';

const NODE_WIDTH = 220;
const NODE_MIN_HEIGHT = 64;
const NODE_PAD_X = 28;
const NODE_PAD_Y = 48;
const SVG_PAD = 24;
const ARROW_SIZE = 6;

// Title + description text layout. Both wrap onto multiple lines instead of
// truncating with an ellipsis (PLT-4994 for descriptions, PLT-5247 for titles),
// so node boxes grow vertically to fit the full text.
const TITLE_FIRST_BASELINE_Y = 22;
const TITLE_LINE_HEIGHT = 16;
// Gap between the last title line's baseline and the first description baseline.
const TITLE_DESC_GAP = 14;
const DESC_LINE_HEIGHT = 13;
const NODE_PAD_BOTTOM = 14;
// Max characters per wrapped line. The box is NODE_WIDTH (220px) wide; titles
// render at 13px bold (~26 chars fit) and descriptions at 10.5px italic (~30).
const TITLE_MAX_CHARS = 26;
const DESC_MAX_CHARS = 30;

// ─── Node colour scheme (PLT-5247) ───────────────────────────────────────────
// Role-based colouring replaces the old per-tier rainbow palette. The previous
// scheme force-coloured the terminal node green, which is both off-brand
// (Upright never uses green for environmental impact — yellow highlight is the
// brand's substitute) and semantically misleading (green reads as
// "positive/good" even on negative endpoints like "human health impacts").
// Three roles, three clearly distinct brand families:
//   - product (start)  → lilac, the brand's categorical accent
//   - intermediate     → neutral gray
//   - impact (end)     → soft yellow highlight, kept as a pale tint so the
//                        endpoint is identifiable without dominating (Milja's
//                        "make the ultimate impact less prominent" point)
// Valence-based tinting (teal=positive / purple=negative) was considered but the
// pathway data carries no valence signal, so a neutral categorical scheme is used.
const PRODUCT_COLOR = { bg: '#E0E7FF', border: '#6A5FFF', text: '#3D29AC' }; // lilac
const INTERMEDIATE_COLOR = { bg: '#F3F4F6', border: '#D1D5DC', text: '#364153' }; // gray
const IMPACT_COLOR = { bg: '#FCFEE8', border: '#C7D43A', text: '#3F3F1A' }; // soft yellow highlight

function getNodeColor(tier: number, maxTier: number) {
  if (tier === 0) return PRODUCT_COLOR;
  if (tier === maxTier) return IMPACT_COLOR;
  return INTERMEDIATE_COLOR;
}

interface LayoutNode {
  id: string;
  titleLines: string[];
  descLines: string[];
  x: number;
  y: number;
  height: number;
  color: { bg: string; border: string; text: string };
}

// Word-wrap a description into lines of at most `maxChars` characters. A single
// word longer than the budget is hard-broken so it can't overflow the box.
function wrapText(text: string, maxChars: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    if (word.length > maxChars) {
      if (current) {
        lines.push(current);
        current = '';
      }
      let rest = word;
      while (rest.length > maxChars) {
        lines.push(rest.slice(0, maxChars));
        rest = rest.slice(maxChars);
      }
      current = rest;
    } else if (!current) {
      current = word;
    } else if (current.length + 1 + word.length <= maxChars) {
      current += ' ' + word;
    } else {
      lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  return lines;
}

// Vertical baseline of the last title line within a node (relative to node top).
function lastTitleBaselineOffset(titleLines: string[]): number {
  return TITLE_FIRST_BASELINE_Y + Math.max(titleLines.length - 1, 0) * TITLE_LINE_HEIGHT;
}

// Baseline of the first description line — sits a fixed gap below the title block.
function descFirstBaselineOffset(titleLines: string[]): number {
  return lastTitleBaselineOffset(titleLines) + TITLE_DESC_GAP;
}

// Box grows to fit the wrapped title and (if any) the description block below it.
function nodeHeight(titleLines: string[], descLines: string[]): number {
  const bottomBaseline =
    descLines.length > 0
      ? descFirstBaselineOffset(titleLines) + (descLines.length - 1) * DESC_LINE_HEIGHT
      : lastTitleBaselineOffset(titleLines);
  return Math.max(NODE_MIN_HEIGHT, bottomBaseline + NODE_PAD_BOTTOM);
}

interface LayoutEdge {
  from: string;
  to: string;
  label?: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  // Resolved during layout: where (if at all) the label is drawn. `showLabel`
  // is false when the label was suppressed to avoid overlapping another one.
  showLabel: boolean;
  labelX: number;
  labelY: number;
}

// ─── Edge routing (PLT-5407) ─────────────────────────────────────────────────
// Every edge used to anchor at its boxes' exact centre-top / centre-bottom and
// drop its label at the geometric midpoint with no collision handling. When a
// tier fanned out/in or edges crossed, the curves collapsed onto shared points
// and the italic labels stacked into an unreadable knot.
//
// The routing below keeps every arrow but never draws anything unreadable:
//   (a) it fans anchor points across each box edge, ordered by the opposite
//       endpoint's x, so parallel and crossing edges separate into distinct
//       lines instead of running on top of each other; and
//   (b) it shows an edge label only when the label's box clears both every other
//       edge's line and every label already shown — so text never lands on a
//       line or on other text. In a crowded band (e.g. a 2×2 crossing) that
//       means no labels are drawn; the box wording still conveys each step.
//       Legibility is the hard requirement.
const EDGE_ANCHOR_SPAN = NODE_WIDTH * 0.6;
const EDGE_ANCHOR_STEP = 26; // px between adjacent anchors before the span clamps
// Approximate glyph box for the 9.5px italic label, used for overlap tests.
const LABEL_CHAR_WIDTH = 5.2;
const LABEL_HEIGHT = 12;
const LABEL_PADDING = 4; // min clear gap (px) a label keeps from lines and other labels

// Symmetric offsets that spread `count` anchors across the box edge, clamped so
// adjacent anchors never sit closer than the diagram can render distinctly.
function anchorOffsets(count: number): number[] {
  if (count <= 1) return [0];
  const span = Math.min(EDGE_ANCHOR_SPAN, (count - 1) * EDGE_ANCHOR_STEP);
  const step = span / (count - 1);
  return Array.from({ length: count }, (_, i) => -span / 2 + i * step);
}

function rectsOverlap(
  a: { x: number; y: number; w: number; h: number },
  b: { x: number; y: number; w: number; h: number },
  pad: number,
): boolean {
  return a.x < b.x + b.w + pad && a.x + a.w + pad > b.x && a.y < b.y + b.h + pad && a.y + a.h + pad > b.y;
}

const cross2d = (ux: number, uy: number, vx: number, vy: number) => ux * vy - uy * vx;

// Do segments AB and CD properly intersect?
function segmentsCross(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  dx: number,
  dy: number,
): boolean {
  const d1 = cross2d(dx - cx, dy - cy, ax - cx, ay - cy);
  const d2 = cross2d(dx - cx, dy - cy, bx - cx, by - cy);
  const d3 = cross2d(bx - ax, by - ay, cx - ax, cy - ay);
  const d4 = cross2d(bx - ax, by - ay, dx - ax, dy - ay);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

// Does segment AB touch the rectangle (inflated by `pad`)? Edges are drawn as
// gentle bezier curves; a straight-segment approximation is close enough to keep
// labels off the lines, and `pad` absorbs the small horizontal bow.
function segmentHitsRect(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  r: { x: number; y: number; w: number; h: number },
  pad: number,
): boolean {
  const minX = r.x - pad,
    maxX = r.x + r.w + pad,
    minY = r.y - pad,
    maxY = r.y + r.h + pad;
  const inside = (x: number, y: number) => x >= minX && x <= maxX && y >= minY && y <= maxY;
  if (inside(ax, ay) || inside(bx, by)) return true;
  return (
    segmentsCross(ax, ay, bx, by, minX, minY, maxX, minY) ||
    segmentsCross(ax, ay, bx, by, maxX, minY, maxX, maxY) ||
    segmentsCross(ax, ay, bx, by, maxX, maxY, minX, maxY) ||
    segmentsCross(ax, ay, bx, by, minX, maxY, minX, minY)
  );
}

// Build the rendered edges from raw pathway edges and resolved node positions:
// fans anchor points so lines never overlap, then shows each label only where it
// clears every line and every other label so text never overlaps anything.
function buildEdges(
  pathway: ProductImpactPathwayData,
  nodePositions: Map<string, { x: number; y: number; height: number }>,
): LayoutEdge[] {
  const centerXById = new Map<string, number>();
  for (const [id, pos] of nodePositions) centerXById.set(id, pos.x + NODE_WIDTH / 2);

  const drawable: number[] = [];
  pathway.edges.forEach((edge, i) => {
    if (nodePositions.has(edge.from) && nodePositions.has(edge.to)) drawable.push(i);
  });

  // Fan anchors out across each box edge, ordered by the opposite endpoint's x so
  // crossings untwist. No edge is dropped: clean fanned lines convey the flow even
  // when two of them cross; only unreadable labels are suppressed (below).
  const outBySrc = new Map<string, number[]>();
  const inByTgt = new Map<string, number[]>();
  for (const i of drawable) {
    const e = pathway.edges[i];
    (outBySrc.get(e.from) ?? outBySrc.set(e.from, []).get(e.from)!).push(i);
    (inByTgt.get(e.to) ?? inByTgt.set(e.to, []).get(e.to)!).push(i);
  }
  const srcAnchorX = new Map<number, number>();
  const tgtAnchorX = new Map<number, number>();
  for (const [src, idxs] of outBySrc) {
    idxs.sort((a, b) => centerXById.get(pathway.edges[a].to)! - centerXById.get(pathway.edges[b].to)!);
    const offs = anchorOffsets(idxs.length);
    idxs.forEach((i, k) => srcAnchorX.set(i, centerXById.get(src)! + offs[k]));
  }
  for (const [tgt, idxs] of inByTgt) {
    idxs.sort((a, b) => centerXById.get(pathway.edges[a].from)! - centerXById.get(pathway.edges[b].from)!);
    const offs = anchorOffsets(idxs.length);
    idxs.forEach((i, k) => tgtAnchorX.set(i, centerXById.get(tgt)! + offs[k]));
  }

  const edges: LayoutEdge[] = drawable.map(i => {
    const edge = pathway.edges[i];
    const fromPos = nodePositions.get(edge.from)!;
    const toPos = nodePositions.get(edge.to)!;
    const x1 = srcAnchorX.get(i)!;
    const x2 = tgtAnchorX.get(i)!;
    const y1 = fromPos.y + fromPos.height;
    const y2 = toPos.y;
    return {
      from: edge.from,
      to: edge.to,
      label: edge.label,
      x1,
      y1,
      x2,
      y2,
      showLabel: false,
      labelX: (x1 + x2) / 2,
      labelY: (y1 + y2) / 2 - 6,
    };
  });

  // Show a label only if its box clears every other edge's line and every label
  // already shown. Most-vertical edges are considered first: they have the
  // clearest lane, so when two labels would compete the upright one wins.
  const order = [...edges].sort((a, b) => Math.abs(a.x2 - a.x1) - Math.abs(b.x2 - b.x1) || a.labelY - b.labelY);
  const placedLabels: { x: number; y: number; w: number; h: number }[] = [];
  for (const e of order) {
    if (!e.label) continue;
    const w = e.label.length * LABEL_CHAR_WIDTH;
    const box = { x: e.labelX - w / 2, y: e.labelY - LABEL_HEIGHT / 2, w, h: LABEL_HEIGHT };
    const hitsLabel = placedLabels.some(p => rectsOverlap(p, box, LABEL_PADDING));
    const hitsLine = edges.some(o => o !== e && segmentHitsRect(o.x1, o.y1, o.x2, o.y2, box, LABEL_PADDING));
    if (hitsLabel || hitsLine) continue;
    e.showLabel = true;
    placedLabels.push(box);
  }

  return edges;
}

function computeLayout(pathway: ProductImpactPathwayData): {
  nodes: LayoutNode[];
  edges: LayoutEdge[];
  width: number;
  height: number;
} {
  const maxTier = Math.max(...pathway.nodes.map(n => n.tier));

  const tierMap = new Map<number, ProductImpactPathwayData['nodes']>();
  for (const node of pathway.nodes) {
    const list = tierMap.get(node.tier) ?? [];
    list.push(node);
    tierMap.set(node.tier, list);
  }

  const tiers = Array.from(tierMap.keys()).sort((a, b) => a - b);
  const maxNodesInTier = Math.max(...tiers.map(t => tierMap.get(t)!.length));
  const totalWidth = maxNodesInTier * (NODE_WIDTH + NODE_PAD_X) - NODE_PAD_X + SVG_PAD * 2;

  // Wrapped titles and descriptions can make nodes taller than NODE_MIN_HEIGHT,
  // so each tier's row height is the tallest node in it, and tiers stack
  // cumulatively.
  const titleLinesById = new Map<string, string[]>();
  const descLinesById = new Map<string, string[]>();
  for (const node of pathway.nodes) {
    titleLinesById.set(node.id, wrapText(node.title, TITLE_MAX_CHARS));
    descLinesById.set(node.id, node.description ? wrapText(node.description, DESC_MAX_CHARS) : []);
  }

  const nodePositions = new Map<string, { x: number; y: number; height: number }>();
  const layoutNodes: LayoutNode[] = [];

  let cursorY = SVG_PAD;
  for (const tier of tiers) {
    const nodesInTier = tierMap.get(tier)!;
    const tierWidth = nodesInTier.length * (NODE_WIDTH + NODE_PAD_X) - NODE_PAD_X;
    const offsetX = (totalWidth - tierWidth) / 2;
    const y = cursorY;
    const rowHeight = Math.max(
      ...nodesInTier.map(n => nodeHeight(titleLinesById.get(n.id)!, descLinesById.get(n.id)!)),
    );

    for (let ni = 0; ni < nodesInTier.length; ni++) {
      const node = nodesInTier[ni];
      const x = offsetX + ni * (NODE_WIDTH + NODE_PAD_X);
      const titleLines = titleLinesById.get(node.id)!;
      const descLines = descLinesById.get(node.id)!;
      const height = nodeHeight(titleLines, descLines);
      nodePositions.set(node.id, { x, y, height });
      layoutNodes.push({
        id: node.id,
        titleLines,
        descLines,
        x,
        y,
        height,
        color: getNodeColor(tier, maxTier),
      });
    }

    cursorY += rowHeight + NODE_PAD_Y;
  }

  const layoutEdges = buildEdges(pathway, nodePositions);

  // cursorY has a trailing NODE_PAD_Y after the last tier; replace it with SVG_PAD.
  const totalHeight = cursorY - NODE_PAD_Y + SVG_PAD;
  return { nodes: layoutNodes, edges: layoutEdges, width: totalWidth, height: totalHeight };
}

function PathwayNodeBox({ node }: { node: LayoutNode }) {
  const rx = 12;
  return (
    <g>
      <rect
        x={node.x}
        y={node.y}
        width={NODE_WIDTH}
        height={node.height}
        rx={rx}
        ry={rx}
        fill={node.color.bg}
        stroke={node.color.border}
        strokeWidth={1.5}
      />
      <text textAnchor="middle" fontWeight={700} fontSize={13} fill={node.color.text}>
        {node.titleLines.map((line, i) => (
          <tspan key={i} x={node.x + NODE_WIDTH / 2} y={node.y + TITLE_FIRST_BASELINE_Y + i * TITLE_LINE_HEIGHT}>
            {line}
          </tspan>
        ))}
      </text>
      {node.descLines.length > 0 && (
        <text textAnchor="middle" fontSize={10.5} fill={node.color.text} opacity={0.75} fontStyle="italic">
          {node.descLines.map((line, i) => (
            <tspan
              key={i}
              x={node.x + NODE_WIDTH / 2}
              y={node.y + descFirstBaselineOffset(node.titleLines) + i * DESC_LINE_HEIGHT}
            >
              {line}
            </tspan>
          ))}
        </text>
      )}
    </g>
  );
}

function PathwayEdgeArrow({ edge }: { edge: LayoutEdge }) {
  const markerId = `pw-arrow-${edge.from}-${edge.to}`;
  const dx = edge.x2 - edge.x1;
  const dy = edge.y2 - edge.y1;
  const midY = edge.y1 + dy / 2;
  const pathD =
    Math.abs(dx) < 5
      ? `M ${edge.x1} ${edge.y1} L ${edge.x2} ${edge.y2 - ARROW_SIZE}`
      : `M ${edge.x1} ${edge.y1} C ${edge.x1} ${midY}, ${edge.x2} ${midY}, ${edge.x2} ${edge.y2 - ARROW_SIZE}`;
  return (
    <g>
      <defs>
        <marker
          id={markerId}
          markerWidth={ARROW_SIZE}
          markerHeight={ARROW_SIZE}
          refX={ARROW_SIZE}
          refY={ARROW_SIZE / 2}
          orient="auto"
        >
          <path d={`M 0 0 L ${ARROW_SIZE} ${ARROW_SIZE / 2} L 0 ${ARROW_SIZE} Z`} fill="#9CA3AF" />
        </marker>
      </defs>
      <path d={pathD} fill="none" stroke="#D1D5DB" strokeWidth={1.5} markerEnd={`url(#${markerId})`} />
      {edge.label && edge.showLabel && (
        <text x={edge.labelX} y={edge.labelY} textAnchor="middle" fontSize={9.5} fill="#9CA3AF" fontStyle="italic">
          {edge.label}
        </text>
      )}
    </g>
  );
}

export function PathwayDiagram({ data }: { data: ProductImpactPathwayData }) {
  const layout = useMemo(() => (data.nodes.length > 0 ? computeLayout(data) : null), [data]);
  if (!layout) return null;
  return (
    <Scroll>
      <svg width={layout.width} height={layout.height} viewBox={`0 0 ${layout.width} ${layout.height}`}>
        {layout.edges.map((edge, i) => (
          <PathwayEdgeArrow key={`edge-${i}`} edge={edge} />
        ))}
        {layout.nodes.map(node => (
          <PathwayNodeBox key={node.id} node={node} />
        ))}
      </svg>
    </Scroll>
  );
}

const Scroll = styled.div`
  overflow-x: auto;
  display: flex;
  justify-content: center;
`;
